import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import type { ComponentType } from 'react';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { showUpgradeToast } from '@/app/components/shared/UpgradeToast';
import { SITE } from '@/app/constants';
import { useDownloadAlertPricing, useGlobalModal, useInterstitialAd, useWatermarkConfirmation } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { addAudioWatermark, downloadFile } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import type { FileItem } from '../types';

export interface DownloadMenuItemConfig {
  key: string;
  onClick: () => void;
  icon: ComponentType<{ className?: string }>;
  label: string;
  badge?: string;
  disabled?: boolean; // 是否禁用
  disabledTip?: string; // 禁用时的提示文案
  /** 以锚点渲染，满足 GPT 插页「点击锚点」触发展示 */
  renderAsAnchor?: boolean;
}

export const useDownload = () => {
  const userInfo = useStore((state) => state.userInfo);
  const isFree = userInfo?.subs_plan === 'free';
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { getIsAlertPricing, markAsShown } = useDownloadAlertPricing();
  const { handleNoWatermarkAction } = useWatermarkConfirmation();
  const { markWatermarkInterstitialShown, canShowWatermarkInterstitialToday } = useInterstitialAd();
  const t = useTranslations();
  const pathname = usePathname();

  // 通用下载函数
  const performDownload = async (downloadUrl: string, filename: string, isNoWatermark: boolean, item: FileItem) => {
    let finalDownloadUrl = downloadUrl;

    try {
      // 如果是音频文件且是中国站，需要添加水印
      if (item.creationType === 'audio' && SITE === 'china') {
        finalDownloadUrl = await addAudioWatermark(downloadUrl);
      }

      // 下载文件，只有当是新生成的 Blob URL 时才自动清理
      downloadFile(finalDownloadUrl, filename, {
        autoRevokeBlobUrl: finalDownloadUrl !== downloadUrl,
      });
    } catch (error) {
      console.error('Download failed:', error);
      if (item.creationId) {
        reportEvent('operation', {
          interaction_type: 'download_failed',
          interaction_position: pathname,
          task_id: item.taskId ?? '',
          creation_id: item.creationId,
          content_type: item.creationType === 'image' || item.creationType === 'audio' ? item.creationType : 'video',
        });
      }
      // 出错时使用原始 URL 下载
      downloadFile(downloadUrl, filename);
      return;
    }

    setTimeout(() => {
      handleDownloadReport(isNoWatermark, item);
    }, 1000);
  };

  // 处理下载事件上报
  const handleDownloadReport = async (noWatermark: boolean, item: FileItem) => {
    if (!item.creationId) return;

    reportEvent('operation', {
      interaction_type: noWatermark ? 'download_no_watermark' : 'download_watermark',
      interaction_position: pathname,
      task_id: item.taskId ?? '',
      creation_id: item.creationId,
      content_type: item.creationType === 'image' || item.creationType === 'audio' ? item.creationType : 'video',
    });

    // 免费用户当日首次下载：提示升级 toast（原先直接弹订阅弹窗，因打断操作改为 toast）
    if (getIsAlertPricing() && isFree) {
      showUpgradeToast({
        message: t('payments.plans.toastNoWatermark'),
        actionText: t('payments.plans.upgradeNow'),
        onAction: () =>
          openPlanDetailDialog({
            scene: PlanDialogScene.Download,
            creationId: item.creationId,
            withWatermark: !noWatermark,
            fromPage: 'download',
          }),
      });
      markAsShown();
      reportEvent('vip_price_toast_exposure', {
        from_page: 'download',
      });
      sendGAEvent('event', 'subscription_exposure', {
        from_page: 'download',
      });
    }
  };

  // 处理有水印下载
  const handleWatermarkDownload = async (item: FileItem) => {
    if (!item.downloadUrl) return;
    markWatermarkInterstitialShown(true);
    await performDownload(item.downloadUrl, item.name, false, item);
  };

  // 处理无水印下载
  const handleNoWatermarkDownload = async (item: FileItem, fromContext = false) => {
    // 有版权限制的视频不允许去水印
    if (item.hasCopyright) {
      return;
    }

    if (isFree) {
      // 免费用户点击弹出订阅弹窗
      openPlanDetailDialog({
        scene: PlanDialogScene.Download,
        creationId: item.creationId,
        fromPage: fromContext ? 'download context menu' : 'download',
      });
      reportEvent('vip_price_modal_exposure', {
        from_page: fromContext ? 'download context menu' : 'download',
      });
      sendGAEvent('event', 'subscription_exposure', {
        from_page: fromContext ? 'download context menu' : 'download',
      });
    } else {
      // 付费用户下载，需要先检查是否需要显示水印确认弹窗
      handleNoWatermarkAction(async () => {
        if (item.nomark_uri) {
          await performDownload(item.nomark_uri, item.name, true, item);
        }
      });
    }
  };

  // 获取下载菜单项配置
  const getDownloadMenuItems = (
    item: FileItem,
    Icon: ComponentType<{ className?: string }>,
    fromContext = false,
  ): DownloadMenuItemConfig[] => {
    const downloadUrl = item.downloadUrl;
    if (!downloadUrl) return [];

    // 显示水印下载选项：仅广告覆盖国家且当天未展示插页时以锚点渲染，否则普通按钮（每天只展示一次）
    const downloadWatermarkItem: DownloadMenuItemConfig = {
      key: 'download-watermark',
      onClick: () => handleWatermarkDownload(item),
      icon: Icon,
      label: t('creations.operations.downloadWatermark'),
      renderAsAnchor: canShowWatermarkInterstitialToday,
    };

    const isNoWatermarkDisabled = item.hasCopyright; // 有版权限制时禁用
    // 显示无水印下载选项
    const downloadNoWatermarkItem: DownloadMenuItemConfig = {
      key: 'download-no-watermark',
      onClick: () => handleNoWatermarkDownload(item, fromContext),
      icon: Icon,
      label: t('creations.operations.downloadNoWatermark'),
      disabled: isNoWatermarkDisabled,
      disabledTip: isNoWatermarkDisabled ? t('sharedComponent.downloadButton.copyrightRestriction') : undefined,
    };

    // 中国站 & 音频 => 只有带水印的
    // 中国站 & 免费用户 & 视频/图片 => 带水印
    // 中国站 & 付费用户 & 视频/图片 => 两者都有
    // 海外站 & 音频 => 只有不带水印的
    // 其他 => 两个都要有
    if (SITE === 'china') {
      if (item.creationType === 'audio') {
        return [downloadWatermarkItem];
      }
      if (isFree) {
        return [
          {
            ...downloadWatermarkItem,
            label: '下载',
          },
        ];
      }
    }
    if (SITE === 'global' && item.creationType === 'audio') {
      return [downloadNoWatermarkItem];
    }
    return [downloadWatermarkItem, downloadNoWatermarkItem];
  };

  return {
    handleWatermarkDownload,
    handleNoWatermarkDownload,
    getDownloadMenuItems,
    isFree,
  };
};
